import h from 'hyperscript';
import chevronLeftIcon from '@disy/cadenza-icons/chevron-left.svg';
import chevronRightIcon from '@disy/cadenza-icons/chevron-right.svg';
import calendarIcon from '@disy/cadenza-icons/calendar.svg';

import flatpickr, { type FlatpickrInstance, type FlatpickrOptions }  from 'ui/flatpickr/flatpickr';
import { setupPopupButton, closePopup } from 'ui/popup/popup';
import { createIconButton } from 'ui/button/button';

import type { EventKey } from 'cadenza/utils/event-util';
import { on, unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';
import {
  formatDateWithJavaFormat,
  parseDateWithJavaFormat
} from 'cadenza/utils/date-time/date-format';
import { icon } from 'cadenza/utils/icon/icon';
import { assertNonNullable } from 'cadenza/utils/custom-error';

import './date-picker.css';
import i18n from './date-picker.properties';

const COMPONENT_NAME = 'd-date-picker';

interface DatePickerPopupOptions {
  inputId?: string;
  initialValue?: string | Date;
  /** A java date format: https://docs.oracle.com/javase/7/docs/api/java/text/SimpleDateFormat.html */
  dateFormat?: string;
  enableTime?: boolean;
  enableSeconds?: boolean;
  isRequired?: boolean;
  disabled?: boolean;
  styleClass?: string;
  name?: string;
  minDate?: Date;
  maxDate?: Date;
}

export class DatePicker extends HTMLElement {

  readonly #dateFormat;
  readonly #input;
  readonly #dropdownTrigger;
  readonly #styleClass;
  readonly #flatpickrComponent: FlatpickrInstance;

  #selectedDate?: Date;
  #disabled!: boolean; // disabled is set in the constructor via setter
  #formEventKey?: EventKey;

  constructor ({
    inputId,
    initialValue,
    enableTime = false,
    enableSeconds = false,
    dateFormat = defaultDateFormat(enableTime, enableSeconds),
    isRequired = false,
    disabled = false,
    name = '',
    styleClass,
    minDate,
    maxDate
  }: DatePickerPopupOptions = {}) {
    super();
    this.#dateFormat = dateFormat;
    this.#styleClass = styleClass;
    this.#input = this.#createInput({ inputId, initialValue, isRequired, name });
    this.#dropdownTrigger = createDropdownTriggerButton();
    this.disabled = disabled;

    const calendar = h(`.${COMPONENT_NAME}--calendar`);
    calendar.classList.toggle(`${COMPONENT_NAME}--calendar-with-time`, enableTime);

    setupPopupButton(
      this.#dropdownTrigger,
      () => h('div', calendar),
      {
        onOpen: () => this.#onPopupOpen(calendar),
        onClose: () => this.#onPopupClose()
      }
    );

    this.#flatpickrComponent = this.#setupFlatpickr(calendar, enableTime, enableSeconds, dateFormat, minDate, maxDate);

    // We need to do this in case the initial value was set.
    if (initialValue) {
      this.#updateSelectedDateIfValidInput();
    }
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    if (this.#styleClass) {
      this.classList.add(this.#styleClass);
    }
    this.append(this.#input, this.#dropdownTrigger);
    const target = this.closest('form');
    if (target) {
      this.#formEventKey = on(target, 'formdata', (event: FormDataEvent) => {
        // Override the raw input value with this.value.
        event.formData.set(this.#input.name, this.value);
      });
    }
  }

  disconnectedCallback () {
    unByKey(this.#formEventKey);
  }

  #createInput ({ inputId, initialValue, isRequired, name }: {
    inputId?: string;
    initialValue?: string | Date;
    isRequired: boolean;
    name: string;
  }) {
    return h<HTMLInputElement>(`input.${COMPONENT_NAME}--input.form-control`, {
      type: 'text',
      id: inputId || uniqueId(),
      placeholder: this.#dateFormat,
      value: initialValue ? this.#formatDateValue(new Date(initialValue)) : '',
      required: isRequired,
      name,
      onkeydown: (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          this.#input.blur();
        }
      },
      onblur: () => {
        this.#updateSelectedDateIfValidInput();
      }
    });
  }

  #onPopupOpen (calendar: HTMLElement) {
    this.#setDatePickerDate(this.#selectedDate);

    // Add missing form-control
    // default control number input arrows don't work for switching year, we need to style built-in arrows
    calendar.querySelectorAll('.numInput:not(.cur-year), .flatpickr-monthDropdown-months')
      .forEach(input => input.classList.add('form-control'));
  }

  #onPopupClose () {
    const selectedDate = this.#getSelectedDate();
    if (this.#selectedDate?.getTime() !== selectedDate?.getTime()) {
      this.#selectedDate = selectedDate;
      this.#setInputValue(selectedDate);
      this.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  #setupFlatpickr (
    calendar: HTMLElement,
    enableTime: boolean,
    enableSeconds: boolean,
    dateFormat: string,
    minDate?: Date,
    maxDate?: Date
  ) {
    const flatPickrConfig: FlatpickrOptions = {
      allowInput: true,
      appendTo: calendar,
      clickOpens: false,
      closeOnSelect: false,
      enableTime,
      enableSeconds,
      inline: true,
      mode: 'single',
      time_24hr: !dateFormat.includes('a'),
      plugins: [],
      prevArrow: createIconButton(icon(chevronLeftIcon, { size: 'l' }),
        i18n('prev'),
        { variant: 'borderless' }).outerHTML,
      nextArrow: createIconButton(icon(chevronRightIcon, { size: 'l' }),
        i18n('next'),
        { variant: 'borderless' }).outerHTML,
      onClose: () => closePopup(),
      maxDate,
      minDate,
      // On click of a date in the date picker, take over the selected date and time.
      onChange: () => this.#setInputValue(this.#getSelectedDate())
    };

    const input = h('input.visuallyhidden');
    calendar.append(input);

    return createFlatpickrWithTimeLabels(input, flatPickrConfig);
  }

  get disabled () {
    return this.#disabled;
  }

  set disabled (value) {
    this.#disabled = value;
    this.#input.disabled = value;
    this.#dropdownTrigger.disabled = value;
  }

  #parseDate (date: string) {
    return parseDateWithJavaFormat(date.trim(), this.#dateFormat);
  }

  #formatDateValue (date?: Date) {
    return date
      ? formatDateWithJavaFormat(date, this.#dateFormat)
      : '';
  }

  #updateSelectedDateIfValidInput = () => {
    // If the user clears the input field we want to clear the date on date picker.
    const hasCleared = this.#selectedDate && this.#input.value === '';
    if (hasCleared) {
      this.#selectedDate = undefined;
      this.#dispatchChangeEvent();
      return;
    }

    const newDate = this.#parseDate(this.#input.value);
    const isValidDate = newDate.toString() !== 'Invalid Date';

    if (isValidDate) {
      const hasChanged = this.#selectedDate?.getTime() !== newDate.getTime();
      if (hasChanged) {
        this.#selectedDate = newDate;
        this.#setInputValue(newDate);
        this.#dispatchChangeEvent();
      }
    } else {
      this.#input.value = this.#formatDateValue(this.#selectedDate); // Reset to previous date.
    }
  };

  #dispatchChangeEvent () {
    this.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #getSelectedDate () {
    return this.#flatpickrComponent.selectedDates[0];
  }

  #setInputValue (date?: Date) {
    this.value = this.#formatDateValue(date);
  }

  #setDatePickerDate (date?: Date) {
    if (date) {
      this.#flatpickrComponent.setDate(date);
    } else {
      this.#flatpickrComponent.clear();
    }
  }

  get value (): string {
    return this.#selectedDate?.toISOString() ?? '';
  }

  /**
   * Set the date value as a formatted date
   *
   * Note: the getter and setter for value are currently not equivalent,
   * as the getter returns the selected date formatted as ISO string.
   *
   * @param date - Date formatted using the same date format as is used by this date picker instance
   */
  set value (date: string | null) {
    this.#input.value = date ?? '';
    this.#updateSelectedDateIfValidInput();
  }

  get valueAsDate () {
    return this.#selectedDate;
  }

  set valueAsDate (date: Date | undefined) {
    this.value = this.#formatDateValue(date);
  }

}

customElements.define(COMPONENT_NAME, DatePicker);

function defaultDateFormat (enableTime: boolean, enableSeconds: boolean) {
  if (enableTime) {
    return enableSeconds ? 'dd.MM.yyyy HH:mm:ss' : 'dd.MM.yyyy HH:mm';
  } else {
    return 'dd.MM.yyyy';
  }
}

function createDropdownTriggerButton () {
  return createIconButton(
    icon(calendarIcon),
    i18n('selectDate.button'),
    { variant: 'borderless', styleClass: `${COMPONENT_NAME}--button` }
  );
}

export function createFlatpickrWithTimeLabels (
  input: HTMLElement,
  flatPickrConfig: FlatpickrOptions
) {
  const flatpickrComponent = flatpickr(input, flatPickrConfig);
  setupTimeInputFields(flatpickrComponent);
  return flatpickrComponent;
}

function setupTimeInputFields (flatpickrComponent: FlatpickrInstance) {
  const timeElements = {
    hours: flatpickrComponent.hourElement,
    minutes: flatpickrComponent.minuteElement,
    seconds: flatpickrComponent.secondElement
  };

  for (const timeUnit in timeElements) {
    const timeUnitKey = timeUnit as keyof typeof timeElements;
    const input = timeElements[timeUnitKey];
    if (input) {
      input.classList.add('form-control'); // if not present, up/down arrows are hidden
      addTimeLabel(input, timeUnit);
      setMinMaxLimits(input, timeUnit);
    }
  }
}

function addTimeLabel (input: HTMLInputElement, timeUnit: string) {
  const parentElement = input.parentElement;
  assertNonNullable(parentElement);
  const label = h(
    `label.bold.${COMPONENT_NAME}--time-unit-label`,
    i18n(`time.${timeUnit}.label`)
  );
  parentElement.insertBefore(label, input);
}

function setMinMaxLimits (input: HTMLInputElement, timeUnit: string) {
  input.min = '0';
  input.max = timeUnit === 'hours' ? '23' : '59';
}
